import type { TelecallerHello } from "../../voice/telecaller.types";

// ---------------------------------------------------------------------------
// TelecallerCard — who the AI is talking to, while the call is live. A glance
// for the colleague listening in: stage, counsellor, recorded preferences,
// deals. Nothing here is spoken; the AI has its own (fuller) brief.
// ---------------------------------------------------------------------------

export default function TelecallerCard({ hello, language }: { hello: TelecallerHello; language: "ml" | "en" }) {
  const ml = language === "ml";
  const L = hello.lead;
  // Defensive: an older server (or a shape slip) must never crash the call.
  const fields = Object.entries(hello.fields ?? {}).slice(0, 6);
  const assignees = hello.assignees ?? [];
  const deals = hello.deals ?? [];
  return (
    <div className="xv-rec" style={{ display: "block" }}>
      <div className="xv-rec-top" style={{ alignItems: "flex-start" }}>
        <div className="xv-rec-meta" style={{ flex: 1, minWidth: 0 }}>
          <div className="xv-rec-title">
            {L?.name ?? ""}
            <span style={{ opacity: 0.55, fontWeight: 400 }}> · {L?.stage ?? ""} › {L?.status ?? ""}</span>
          </div>
          <div className="xv-rec-sub">
            {hello.label}
            {hello.branch ? ` · ${hello.branch.name}` : ""}
            {assignees.length ? ` · ${assignees.map((a) => a.name).join(", ")}` : ml ? ` · ${hello.human ?? "counsellor"} ഇല്ല` : ` · no ${hello.human ?? "counsellor"} yet`}
          </div>
        </div>
        {hello.practice && (
          <span className="xv-pill" style={{ height: 26, fontSize: 11, cursor: "default", color: "#ffd9a8", borderColor: "rgba(255,178,122,0.5)" }}>
            {ml ? "പ്രാക്ടീസ്" : "Practice"}
          </span>
        )}
        <span className="xv-pill" style={{ height: 26, fontSize: 11, cursor: "default" }}>
          {hello.agentName} · {hello.voice}
          {hello.language === "ml" && hello.dialect
            ? ` · ${hello.dialect === "mappila" ? (hello.greeting === "salaam" ? "Mappila · സലാം" : "Mappila") : hello.dialect === "kozhikode" ? (ml ? "കോഴിക്കോട്" : "Kozhikode") : "Standard"}`
            : ""}
          {hello.pace === "fast" ? " · fast" : hello.pace === "natural" ? (ml ? " · സാധാരണ speed" : " · natural pace") : ""}
        </span>
      </div>
      {(fields.length > 0 || deals.length > 0) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12 }}>
          {fields.map(([k, v]) => (
            <span key={k} style={{ padding: "3px 9px", borderRadius: 999, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <span style={{ opacity: 0.6 }}>{k}: </span>
              {v}
            </span>
          ))}
          {deals.map((d) => (
            <span key={d.id} style={{ padding: "3px 9px", borderRadius: 999, background: "rgba(111,185,156,0.16)", border: "1px solid rgba(111,185,156,0.3)" }}>
              {d.name} · {d.stage} › {d.status}
            </span>
          ))}
        </div>
      )}
      <div className="xv-rec-sub" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <span>{ml ? `${hello.calls ?? 0} മുൻ കോളുകൾ` : `${hello.calls ?? 0} previous calls`}</span>
        <span>{ml ? `${hello.pendingFollowUps ?? 0} pending follow-ups` : `${hello.pendingFollowUps ?? 0} pending follow-ups`}</span>
        {(L?.reenquired ?? 0) > 0 && <span>{ml ? `${L.reenquired}× re-enquired` : `re-enquired ${L.reenquired}×`}</span>}
        {hello.practice
          ? <span style={{ color: "#ffd9a8" }}>{ml ? "ഒന്നും save ചെയ്യില്ല — നിങ്ങളാണ് ലീഡ്" : "Nothing is saved — you are the lead"}</span>
          : hello.window && !hello.window.open && <span style={{ color: "#ffb27a" }}>{ml ? "Calling hours-ന് പുറത്ത്" : "Outside calling hours"}</span>}
      </div>
    </div>
  );
}
